import { Node, Edge, SocketDefinition, Point, EdgeRoutingMode } from '../types';
import { encloses } from './nesting';
import { simplifyPolyline } from './polyline';
import type { NodeDirection } from './socketLayout';

/** How far an endpoint may drift before its stored route stops describing it. */
const ROUTE_TOLERANCE = 0.75;

/**
 * The polyline a layout drew for this edge, if it still describes the edge.
 *
 * A stored route is only true of the diagram it was computed for, so it is
 * checked against the sockets it claims to join before being drawn. When the
 * check fails - a node was moved or resized, an end was dragged onto another
 * socket - the edge falls back to being routed from scratch, which is always
 * correct even if it is less pretty than a full layout run would be.
 */
export function autoRoutePoints(edge: Edge, start: Point, end: Point): number[] | null {
    const route = edge.autoRoute;
    if (!route) return null;
    if (Math.abs(route.from.x - start.x) > ROUTE_TOLERANCE || Math.abs(route.from.y - start.y) > ROUTE_TOLERANCE) return null;
    if (Math.abs(route.to.x - end.x) > ROUTE_TOLERANCE || Math.abs(route.to.y - end.y) > ROUTE_TOLERANCE) return null;
    const flat = [start.x, start.y];
    for (const point of route.points) flat.push(point.x, point.y);
    flat.push(end.x, end.y);
    return simplifyPolyline(flat);
}

/** Infer the exit/entry direction vector for a socket based on node direction + socket type. */
export function getSocketExitDir(socketType: 'input' | 'output', nodeDirection: NodeDirection): { dx: number; dy: number } {
    if (nodeDirection === 'ttb') {
        return socketType === 'input' ? { dx: 0, dy: -1 } : { dx: 0, dy: 1 };
    }
    if (nodeDirection === 'rtl') {
        return socketType === 'input' ? { dx: 1, dy: 0 } : { dx: -1, dy: 0 };
    }
    // ltr (default)
    return socketType === 'input' ? { dx: -1, dy: 0 } : { dx: 1, dy: 0 };
}

/**
 * Outward cardinal from the socket's place on the node — not from input/output type.
 * Top sockets exit up, left sockets exit left, even if the port is still untyped.
 */
export function getSocketOutwardDir(
    socket: Pick<SocketDefinition, 'position' | 'tickSide'>,
    nodeSize: { width: number; height: number },
): { dx: number; dy: number } {
    switch (socket.tickSide) {
        case 'up': return { dx: 0, dy: -1 };
        case 'down': return { dx: 0, dy: 1 };
        case 'left': return { dx: -1, dy: 0 };
        case 'right': return { dx: 1, dy: 0 };
    }
    const vx = socket.position.x - nodeSize.width / 2;
    const vy = socket.position.y - nodeSize.height / 2;
    if (Math.abs(vx) >= Math.abs(vy)) {
        return vx >= 0 ? { dx: 1, dy: 0 } : { dx: -1, dy: 0 };
    }
    return vy >= 0 ? { dx: 0, dy: 1 } : { dx: 0, dy: -1 };
}

/** Saturated bezier handle length — long spans stay compact instead of looping. */
export function bezierControlOffset(dist: number, curveStrength = 1): number {
    const raw = 24 + Math.min(Math.max(dist, 0), 480) * 0.16;
    return Math.min(96, Math.max(22, raw)) * curveStrength;
}

export function getEdgeGeometry(
    edge: Edge,
    sourceNode: Node,
    targetNode: Node,
    sourceSocket: SocketDefinition,
    targetSocket: SocketDefinition,
    laneShift = 0,
) {
    const startX = sourceNode.position.x + sourceSocket.position.x;
    const startY = sourceNode.position.y + sourceSocket.position.y;
    const endX = targetNode.position.x + targetSocket.position.x;
    const endY = targetNode.position.y + targetSocket.position.y;
    const waypoints = edge.waypoints ?? [];

    const srcExit = getSocketOutwardDir(sourceSocket, sourceNode.size);
    const tgtExit = getSocketOutwardDir(targetSocket, targetNode.size);

    const isVerticalSource = srcExit.dy !== 0;
    const isVerticalTarget = tgtExit.dy !== 0;

    let linePoints: number[];
    let useTension: boolean;
    let arrowPenultimate: Point;
    const endPoint = { x: endX, y: endY };

    // Scale control point offset by distance, then cap so long-haul edges
    // don't loop across empty canvas. Optional laneShift fans parallel curves.
    const curveStrength = edge.curveStrength ?? 1.0;
    const totalDist = Math.sqrt((endX - startX) ** 2 + (endY - startY) ** 2);
    const controlOffset = bezierControlOffset(totalDist, curveStrength);
    const spreadX = -srcExit.dy * laneShift;
    const spreadY = srcExit.dx * laneShift;

    if (waypoints.length > 0) {
        const firstWaypoint = waypoints[0];
        const lastWaypoint = waypoints[waypoints.length - 1];
        const startSegmentDist = Math.sqrt((firstWaypoint.x - startX) ** 2 + (firstWaypoint.y - startY) ** 2);
        const endSegmentDist = Math.sqrt((endX - lastWaypoint.x) ** 2 + (endY - lastWaypoint.y) ** 2);
        const startGuideOffset = Math.min(controlOffset * 0.7, Math.max(18, startSegmentDist * 0.45), startSegmentDist);
        const endGuideOffset = Math.min(controlOffset * 0.7, Math.max(18, endSegmentDist * 0.45), endSegmentDist);
        const startGuide = {
            x: startX + srcExit.dx * startGuideOffset,
            y: startY + srcExit.dy * startGuideOffset,
        };
        const endGuide = {
            x: endX + tgtExit.dx * endGuideOffset,
            y: endY + tgtExit.dy * endGuideOffset,
        };

        linePoints = [
            startX,
            startY,
            startGuide.x,
            startGuide.y,
            ...waypoints.flatMap(p => [p.x, p.y]),
            endGuide.x,
            endGuide.y,
            endX,
            endY,
        ];
        useTension = true;
        arrowPenultimate = endGuide;
    } else if (isVerticalSource && isVerticalTarget) {
        // Both endpoints exit/enter vertically (TTB→TTB).
        // Reverse and sideways hops used to grow 96px handles that looped
        // around the whole diagram; keep those handles short and pull CPs
        // toward the other node so the stroke stays a shallow C, not a halo.
        const dx = endX - startX;
        const dy = endY - startY;
        const aligned = dy * srcExit.dy;
        const sideways = Math.abs(dx) > Math.abs(dy) * 0.85;
        let handle = controlOffset;
        if (aligned < 48) {
            handle = Math.min(handle, 26);
        } else if (sideways) {
            handle = Math.min(handle, 40);
        }
        const pull = (aligned < 48 || sideways) ? 0.22 : 0;
        const detour = (aligned < 48 || sideways) ? Math.sign(dx || 1) * 18 : 0;
        const cp1x = startX + spreadX + dx * pull + detour;
        const cp1y = startY + srcExit.dy * handle + spreadY;
        const cp2x = endX + spreadX - dx * pull + detour;
        const cp2y = endY + tgtExit.dy * handle + spreadY;
        linePoints = [startX, startY, cp1x, cp1y, cp2x, cp2y, endX, endY];
        useTension = false;
        arrowPenultimate = { x: cp2x, y: cp2y };
    } else if (isVerticalSource !== isVerticalTarget) {
        // Mixed (e.g. LTR→TTB): L-shaped bezier following each socket's exit direction.
        const cp1x = startX + srcExit.dx * controlOffset + spreadX;
        const cp1y = startY + srcExit.dy * controlOffset + spreadY;
        const cp2x = endX + tgtExit.dx * controlOffset + spreadX;
        const cp2y = endY + tgtExit.dy * controlOffset + spreadY;
        linePoints = [startX, startY, cp1x, cp1y, cp2x, cp2y, endX, endY];
        useTension = false;
        arrowPenultimate = { x: cp2x, y: cp2y };
    } else {
        // Both horizontal (LTR/RTL) — same reverse/sideways tightening.
        const dx = endX - startX;
        const dy = endY - startY;
        const aligned = dx * srcExit.dx;
        const sideways = Math.abs(dy) > Math.abs(dx) * 0.85;
        let handle = controlOffset;
        if (aligned < 48) {
            handle = Math.min(handle, 26);
        } else if (sideways) {
            handle = Math.min(handle, 40);
        }
        const pull = (aligned < 48 || sideways) ? 0.22 : 0;
        const detour = (aligned < 48 || sideways) ? Math.sign(dy || 1) * 18 : 0;
        const cp1x = startX + srcExit.dx * handle + spreadX;
        const cp1y = startY + spreadY + dy * pull + detour;
        const cp2x = endX + tgtExit.dx * handle + spreadX;
        const cp2y = endY + spreadY - dy * pull + detour;
        linePoints = [startX, startY, cp1x, cp1y, cp2x, cp2y, endX, endY];
        useTension = false;
        arrowPenultimate = { x: cp2x, y: cp2y };
    }

    // Calculate label position
    let labelPos: Point | null = null;
    if (useTension && linePoints.length >= 4) {
        // Path length approximation over polyline segments
        let totalLen = 0;
        const segs: { len: number, dx: number, dy: number, x1: number, y1: number }[] = [];
        for (let i = 0; i < linePoints.length - 2; i += 2) {
            const segDx = linePoints[i + 2] - linePoints[i];
            const segDy = linePoints[i + 3] - linePoints[i + 1];
            const len = Math.sqrt(segDx * segDx + segDy * segDy);
            if (len > 0) {
                totalLen += len;
                segs.push({ len, dx: segDx, dy: segDy, x1: linePoints[i], y1: linePoints[i + 1] });
            }
        }

        if (totalLen === 0) {
            labelPos = { x: linePoints[0], y: linePoints[1] };
        } else {
            let target = totalLen / 2;
            for (const seg of segs) {
                if (target <= seg.len) {
                    const t = target / seg.len;
                    labelPos = { x: seg.x1 + seg.dx * t, y: seg.y1 + seg.dy * t };
                    break;
                }
                target -= seg.len;
            }
            if (!labelPos) {
                labelPos = { x: linePoints[linePoints.length - 2], y: linePoints[linePoints.length - 1] };
            }
        }
    } else if (!useTension && linePoints.length >= 8) {
        // Cubic bezier midpoint (t=0.5)
        const [p0x, p0y, p1x, p1y, p2x, p2y, p3x, p3y] = linePoints;
        const t = 0.5;
        const mt = 1 - t;
        const x = mt * mt * mt * p0x + 3 * mt * mt * t * p1x + 3 * mt * t * t * p2x + t * t * t * p3x;
        const y = mt * mt * mt * p0y + 3 * mt * mt * t * p1y + 3 * mt * t * t * p2y + t * t * t * p3y;
        labelPos = { x, y };
    }

    // Fallback: if labelPos is still null (e.g. short/degenerate edges), use start/end midpoint
    if (!labelPos) {
        labelPos = { x: (startX + endX) / 2, y: (startY + endY) / 2 };
    }

    return { linePoints, useTension, arrowPenultimate, endPoint, labelPos };
}

/**
 * Flatten a cubic bezier (8 numbers) into a polyline so hit-testing follows
 * the visible curve instead of the control-point hull.
 */
export function sampleDisplayPolyline(pts: number[], useBezier: boolean, samples = 48): number[] {
    if (!useBezier || pts.length !== 8) return pts;
    const [x0, y0, cp1x, cp1y, cp2x, cp2y, x1, y1] = pts;
    const result: number[] = [];
    for (let i = 0; i <= samples; i++) {
        const t = i / samples;
        const mt = 1 - t;
        result.push(
            mt * mt * mt * x0 + 3 * mt * mt * t * cp1x + 3 * mt * t * t * cp2x + t * t * t * x1,
            mt * mt * mt * y0 + 3 * mt * mt * t * cp1y + 3 * mt * t * t * cp2y + t * t * t * y1,
        );
    }
    return result;
}

// ── Orthogonal (Manhattan) routing ──────────────────────────────────────

const SNAP_GRID = 20;
const ORTHO_STUB = 32;
export const ORTHO_LANE = 36;
export const BEZIER_LANE = 28;
const CHANNEL_PROXIMITY = 40;
const SKIP_GAP = 300;
const CLEAR_STEP = 16;
const CLEAR_PAD = 12;
/** Match the frame chrome ELK pads with, so wires sit outside subgraph outlines. */
const CLUSTER_PAD_X = 32;
const CLUSTER_PAD_TOP = 34;
const CLUSTER_PAD_BOT = 28;
/** Park gutters outside the frame chrome, not on the border. */
const CLUSTER_LIP = 28;

interface ClusterBox {
    id: string;
    x: number;
    y: number;
    w: number;
    h: number;
}

interface GutterHint {
    preferDest: boolean;
    destLeft?: number;
    destRight?: number;
    srcLeft?: number;
    srcRight?: number;
}

function clusterBoxesFromNodes(nodes: Node[]): Map<string, ClusterBox> {
    const map = new Map<string, ClusterBox>();
    for (const node of nodes) {
        if (!node.parentGroupId) continue;
        if (node.type === 'group' || node.type === 'text' || node.type === 'note') continue;
        const x0 = node.position.x;
        const y0 = node.position.y;
        const x1 = x0 + node.size.width;
        const y1 = y0 + node.size.height;
        const existing = map.get(node.parentGroupId);
        if (!existing) {
            map.set(node.parentGroupId, {
                id: node.parentGroupId, x: x0, y: y0, w: node.size.width, h: node.size.height,
            });
            continue;
        }
        const nx = Math.min(existing.x, x0);
        const ny = Math.min(existing.y, y0);
        const r = Math.max(existing.x + existing.w, x1);
        const b = Math.max(existing.y + existing.h, y1);
        map.set(node.parentGroupId, { id: node.parentGroupId, x: nx, y: ny, w: r - nx, h: b - ny });
    }
    map.forEach((box, id) => {
        map.set(id, {
            id,
            x: box.x - CLUSTER_PAD_X,
            y: box.y - CLUSTER_PAD_TOP,
            w: box.w + CLUSTER_PAD_X * 2,
            h: box.h + CLUSTER_PAD_TOP + CLUSTER_PAD_BOT,
        });
    });
    return map;
}

/** Other subgraph frames become solid obstacles so long-hauls follow cluster outlines. */
function phantomClusterNodes(nodes: Node[], sourceNodeId: string, targetNodeId: string): Node[] {
    const src = nodes.find(node => node.id === sourceNodeId);
    const tgt = nodes.find(node => node.id === targetNodeId);
    if (!src || !tgt) return [];
    if (src.parentGroupId === tgt.parentGroupId) return [];
    const skip = new Set(
        [src.parentGroupId, tgt.parentGroupId].filter((id): id is string => Boolean(id)),
    );
    const phantoms: Node[] = [];
    clusterBoxesFromNodes(nodes).forEach((box, gid) => {
        if (skip.has(gid)) return;
        phantoms.push({
            id: `__cluster:${gid}`,
            type: 'service',
            position: { x: box.x, y: box.y },
            size: { width: box.w, height: box.h },
            label: '',
            sockets: [],
        });
    });
    return phantoms;
}

/**
 * The shapes drawn around an endpoint are not obstacles for that wire.
 *
 * A wire has to leave whatever it starts inside, and arrive inside whatever it
 * ends in; a box drawn around either end is a container to be crossed, not a
 * wall to be walked around. Treating it as a wall is what sends a wire out of
 * its box, along the outside and back in again, when the two ends were sitting
 * one above the other all along.
 *
 * Frames already work this way. This is the same rule for shapes, which express
 * containment only by where they are drawn, and it cascades on its own: every
 * shape enclosing an endpoint is dropped, however many are stacked up.
 */
function withoutEnclosing(nodes: Node[], sourceNodeId: string, targetNodeId: string): Node[] {
    const src = nodes.find(node => node.id === sourceNodeId);
    const tgt = nodes.find(node => node.id === targetNodeId);
    if (!src && !tgt) return nodes;
    const walls = new Set<string>();
    for (const node of nodes) {
        if (node.id === sourceNodeId || node.id === targetNodeId) continue;
        if ((src && encloses(node, src)) || (tgt && encloses(node, tgt))) walls.add(node.id);
    }
    return walls.size === 0 ? nodes : nodes.filter(node => !walls.has(node.id));
}

function routingObstacles(nodes: Node[], sourceNodeId: string, targetNodeId: string): Node[] {
    // Phantoms are worked out from the whole drawing; only the list of things
    // this wire has to dodge gets its enclosing shapes taken out.
    const phantoms = phantomClusterNodes(nodes, sourceNodeId, targetNodeId);
    const open = withoutEnclosing(nodes, sourceNodeId, targetNodeId);
    return phantoms.length ? open.concat(phantoms) : open;
}

function snapToGrid(v: number): number {
    return Math.round(v / SNAP_GRID) * SNAP_GRID;
}

function spansOverlap(a0: number, a1: number, b0: number, b1: number, pad = 10): boolean {
    const aMin = Math.min(a0, a1) - pad;
    const aMax = Math.max(a0, a1) + pad;
    const bMin = Math.min(b0, b1) - pad;
    const bMax = Math.max(b0, b1) + pad;
    return aMin < bMax && bMin < aMax;
}

/**
 * Place the orthogonal trunk near the source (short stub), not halfway across
 * a long gap — that midpoint bus is what made dense TB diagrams look tangled.
 */
function axisTrunk(
    start: number,
    end: number,
    srcDir: number,
    tgtDir: number,
): { mid: number; dir: number; aligned: number } {
    const travel = end - start;
    const dir = Math.sign(srcDir || travel || 1) || 1;
    const room = Math.abs(travel);
    const aligned = travel * dir;
    const stub = Math.min(ORTHO_STUB, Math.max(16, room * 0.16));
    let mid = start + dir * stub;
    if (aligned > 0 && srcDir * tgtDir < 0) {
        const lo = Math.min(start, end) + 12;
        const hi = Math.max(start, end) - 12;
        mid = Math.max(lo, Math.min(hi, mid));
    }
    if (aligned > SKIP_GAP) {
        const deeper = start + dir * Math.min(
            Math.max(ORTHO_STUB + CHANNEL_PROXIMITY + 12, room * 0.22),
            room - 48,
        );
        mid = dir > 0 ? Math.max(mid, deeper) : Math.min(mid, deeper);
        const lo = Math.min(start, end) + 12;
        const hi = Math.max(start, end) - 12;
        if (hi > lo) mid = Math.max(lo, Math.min(hi, mid));
    }
    return { mid, dir, aligned };
}

export function orthogonalTrunk(
    startX: number, startY: number,
    endX: number, endY: number,
    srcExit: { dx: number; dy: number },
    tgtExit: { dx: number; dy: number },
): { kind: 'hvh' | 'vhv' | 'mixed'; mid: number; span0: number; span1: number; dir: number; longHaul: boolean } {
    const srcHoriz = srcExit.dx !== 0;
    const tgtHoriz = tgtExit.dx !== 0;
    if (srcHoriz && tgtHoriz) {
        const { mid, dir, aligned } = axisTrunk(startX, endX, srcExit.dx, tgtExit.dx);
        const span = Math.abs(endY - startY);
        return { kind: 'hvh', mid, span0: startY, span1: endY, dir, longHaul: aligned > SKIP_GAP || (aligned < 48 && span > 200) };
    }
    if (!srcHoriz && !tgtHoriz) {
        const { mid, dir, aligned } = axisTrunk(startY, endY, srcExit.dy, tgtExit.dy);
        const span = Math.abs(endX - startX);
        return { kind: 'vhv', mid, span0: startX, span1: endX, dir, longHaul: aligned > SKIP_GAP || (aligned < 48 && span > 200) };
    }
    return { kind: 'mixed', mid: 0, span0: 0, span1: 0, dir: 1, longHaul: false };
}

function columnBlocked(
    x: number,
    y0: number,
    y1: number,
    nodes: Node[],
    sourceNodeId: string,
    targetNodeId: string,
): boolean {
    const lo = Math.min(y0, y1) - 4;
    const hi = Math.max(y0, y1) + 4;
    for (const node of nodes) {
        if (node.id === sourceNodeId || node.id === targetNodeId) continue;
        if (node.type === 'group' || node.type === 'text' || node.type === 'note') continue;
        if (x < node.position.x - CLEAR_PAD || x > node.position.x + node.size.width + CLEAR_PAD) continue;
        const n0 = node.position.y;
        const n1 = node.position.y + node.size.height;
        if (hi >= n0 && lo <= n1) return true;
    }
    return false;
}

function rowBlocked(
    y: number,
    x0: number,
    x1: number,
    nodes: Node[],
    sourceNodeId: string,
    targetNodeId: string,
): boolean {
    const lo = Math.min(x0, x1) - 4;
    const hi = Math.max(x0, x1) + 4;
    for (const node of nodes) {
        if (node.id === sourceNodeId || node.id === targetNodeId) continue;
        if (node.type === 'group' || node.type === 'text' || node.type === 'note') continue;
        if (y < node.position.y - CLEAR_PAD || y > node.position.y + node.size.height + CLEAR_PAD) continue;
        const n0 = node.position.x;
        const n1 = node.position.x + node.size.width;
        if (hi >= n0 && lo <= n1) return true;
    }
    return false;
}

function clampBetween(value: number, a: number, b: number, pad = 12): number {
    const lo = Math.min(a, b) + pad;
    const hi = Math.max(a, b) - pad;
    if (hi <= lo) return (a + b) / 2;
    return Math.max(lo, Math.min(hi, value));
}

function cappedShift(shift: number, start: number, end: number): number {
    const room = Math.abs(end - start);
    const maxShift = Math.max(0, room - 28);
    return Math.max(-maxShift, Math.min(maxShift, shift));
}

/** True when a candidate X sits on the far side of the destination (go past, then back). */
function overshootsDest(start: number, end: number, via: number, slack = 8): boolean {
    return (via - end) * (start - end) < 0 && Math.abs(via - end) > slack && Math.abs(start - end) > 8;
}

function isNearX(a: number, b?: number): boolean {
    return b != null && Math.abs(a - b) < 3;
}

function gutterOvershoots(startX: number, endX: number, via: number, hint?: GutterHint): boolean {
    if (hint && (isNearX(via, hint.srcLeft) || isNearX(via, hint.srcRight))) {
        return overshootsDest(startX, endX, via, 80);
    }
    return overshootsDest(startX, endX, via);
}

function farSrcWrap(startX: number, endX: number, via: number, hint?: GutterHint): boolean {
    if (!hint || hint.srcLeft == null || hint.srcRight == null) return false;
    const inside = endX > hint.srcLeft + 8 && endX < hint.srcRight - 8;
    if (!inside) return false;
    if (startX < endX) return isNearX(via, hint.srcLeft);
    return isNearX(via, hint.srcRight);
}

function overlap1D(a0: number, a1: number, b0: number, b1: number): number {
    const lo = Math.max(Math.min(a0, a1), b0);
    const hi = Math.min(Math.max(a0, a1), b1);
    return Math.max(0, hi - lo);
}

function towardDestX(startX: number, endX: number): number {
    return endX + (Math.sign(startX - endX) || 1) * 16;
}

function outsideClusterX(
    x: number,
    box: ClusterBox,
    y0: number,
    y1: number,
    nodes: Node[],
    sourceNodeId: string,
    targetNodeId: string,
    startX: number,
    endX: number,
): number {
    const left = box.x - CLUSTER_LIP;
    const right = box.x + box.w + CLUSTER_LIP;
    const inside = x > box.x + 8 && x < box.x + box.w - 8;
    if (!inside) return overshootsDest(startX, endX, x) ? towardDestX(startX, endX) : x;
    const sides = [left, right];
    const clear = sides.filter(s => !columnBlocked(s, y0, y1, nodes, sourceNodeId, targetNodeId));
    const pool = (clear.length ? clear : sides).filter(s => !overshootsDest(startX, endX, s));
    if (pool.length === 0) {
        return overshootsDest(startX, endX, x) ? towardDestX(startX, endX) : x;
    }
    pool.sort((a, b) => Math.abs(a - x) - Math.abs(b - x) || Math.abs(a - endX) - Math.abs(b - endX));
    return pool[0];
}

function mergeXIntervals(spans: { lo: number; hi: number }[]): { lo: number; hi: number }[] {
    if (spans.length === 0) return [];
    const sorted = [...spans].sort((a, b) => a.lo - b.lo);
    const out = [{ ...sorted[0] }];
    for (let i = 1; i < sorted.length; i++) {
        const last = out[out.length - 1];
        if (sorted[i].lo <= last.hi + 2) last.hi = Math.max(last.hi, sorted[i].hi);
        else out.push({ ...sorted[i] });
    }
    return out;
}

function obstacleIntervalsX(
    y0: number,
    y1: number,
    nodes: Node[],
    sourceNodeId: string,
    targetNodeId: string,
): { lo: number; hi: number }[] {
    const yLo = Math.min(y0, y1);
    const yHi = Math.max(y0, y1);
    const spans: { lo: number; hi: number }[] = [];
    for (const node of nodes) {
        if (node.id === sourceNodeId || node.id === targetNodeId) continue;
        if (node.type === 'group' || node.type === 'text' || node.type === 'note') continue;
        const n0 = node.position.y;
        const n1 = node.position.y + node.size.height;
        if (yHi < n0 - CLEAR_PAD || yLo > n1 + CLEAR_PAD) continue;
        spans.push({
            lo: node.position.x - CLEAR_PAD,
            hi: node.position.x + node.size.width + CLEAR_PAD,
        });
    }
    return mergeXIntervals(spans);
}

/** Clear column between source-cluster members, close to the source X. */
function nearestMemberGapX(
    startX: number,
    endX: number,
    y0: number,
    y1: number,
    box: ClusterBox,
    members: Node[],
    sourceNodeId: string,
    targetNodeId: string,
): number | null {
    const innerLo = box.x + 8;
    const innerHi = box.x + box.w - 8;
    const intervals = obstacleIntervalsX(y0, y1, members, sourceNodeId, targetNodeId);
    const candidates: number[] = [];
    const consider = (x: number) => {
        if (!Number.isFinite(x)) return;
        if (x < innerLo || x > innerHi) return;
        if (Math.abs(x - startX) > 160) return;
        if (overshootsDest(startX, endX, x, 16)) return;
        if (!columnBlocked(x, y0, y1, members, sourceNodeId, targetNodeId)) candidates.push(x);
    };
    consider(startX);
    for (let i = 0; i < intervals.length - 1; i++) {
        const gapLo = intervals[i].hi;
        const gapHi = intervals[i + 1].lo;
        if (gapHi - gapLo < 16) continue;
        consider((gapLo + gapHi) / 2);
        consider(Math.max(gapLo + 6, Math.min(gapHi - 6, startX)));
        if (startX < endX) consider(gapLo + 8);
        else consider(gapHi - 8);
    }
    if (intervals.length > 0) {
        consider(intervals[0].lo - 8);
        consider(intervals[intervals.length - 1].hi + 8);
    }
    if (candidates.length === 0) return null;
    const unique = [...new Set(candidates.map(x => Math.round(x * 2) / 2))];
    unique.sort((a, b) => {
        const da = Math.abs(a - startX);
        const db = Math.abs(b - startX);
        if (Math.abs(da - db) > 4) return da - db;
        return Math.abs(a - endX) - Math.abs(b - endX);
    });
    return unique[0];
}

/** Dest-below exit column: prefer the dest X (clubbed comb) then a dest-facing margin. */
function destBelowChannelX(
    startX: number,
    endX: number,
    y0: number,
    y1: number,
    box: ClusterBox,
    members: Node[],
    sourceNodeId: string,
    targetNodeId: string,
): number | null {
    const innerLo = box.x + 8;
    const innerHi = box.x + box.w - 8;
    const candidates: number[] = [];
    const consider = (x: number) => {
        if (!Number.isFinite(x)) return;
        if (x < innerLo || x > innerHi) return;
        if (overshootsDest(startX, endX, x, 16)) return;
        if (!columnBlocked(x, y0, y1, members, sourceNodeId, targetNodeId)) candidates.push(x);
    };
    consider(endX);
    if (startX < endX) consider(innerHi - 6);
    else consider(innerLo + 6);
    const intervals = obstacleIntervalsX(y0, y1, members, sourceNodeId, targetNodeId);
    for (let i = 0; i < intervals.length - 1; i++) {
        const gapLo = intervals[i].hi;
        const gapHi = intervals[i + 1].lo;
        if (gapHi - gapLo < 16) continue;
        consider(startX < endX ? gapHi - 8 : gapLo + 8);
        consider((gapLo + gapHi) / 2);
    }
    if (intervals.length > 0) {
        consider(startX < endX ? intervals[intervals.length - 1].hi + 8 : intervals[0].lo - 8);
    }
    consider(startX);
    if (candidates.length === 0) return null;
    const unique = [...new Set(candidates.map(x => Math.round(x * 2) / 2))];
    unique.sort((a, b) => {
        const da = Math.abs(a - endX);
        const db = Math.abs(b - endX);
        if (Math.abs(da - db) > 4) return da - db;
        return Math.abs(a - startX) - Math.abs(b - startX);
    });
    return unique[0];
}

/**
 * Clear vertical channel: prefer a gap between obstacles (Mermaid-style
 * inter-node alley), then the nearest outside edge. Never walk past the
 * destination to the far side of the whole rank.
 */
function nearestClearVerticalX(
    startX: number,
    endX: number,
    y0: number,
    y1: number,
    nodes: Node[],
    sourceNodeId: string,
    targetNodeId: string,
    laneIndex: number,
    hint?: GutterHint,
): number {
    const mid = (startX + endX) / 2;
    const facing = hint?.preferDest
        ? (startX < endX
            ? (hint.destLeft ?? endX) - CLUSTER_LIP
            : (hint.destRight ?? endX) + CLUSTER_LIP)
        : mid;
    const intervals = obstacleIntervalsX(y0, y1, nodes, sourceNodeId, targetNodeId);
    const candidates: number[] = [];

    const consider = (x: number) => {
        if (!Number.isFinite(x)) return;
        if (!columnBlocked(x, y0, y1, nodes, sourceNodeId, targetNodeId)) candidates.push(x);
    };

    consider(startX);
    consider(endX);
    if (hint?.preferDest) {
        if (hint.destLeft != null) {
            const x = hint.destLeft - CLUSTER_LIP;
            if (!overshootsDest(startX, endX, x)) consider(x);
        }
        if (hint.destRight != null) {
            const x = hint.destRight + CLUSTER_LIP;
            if (!overshootsDest(startX, endX, x)) consider(x);
        }
        if (!overshootsDest(startX, endX, facing)) consider(facing);
        if (hint.srcLeft != null) consider(hint.srcLeft);
        if (hint.srcRight != null) consider(hint.srcRight);
    }
    for (let i = 0; i < intervals.length - 1; i++) {
        const gapLo = intervals[i].hi;
        const gapHi = intervals[i + 1].lo;
        if (gapHi - gapLo < 14) continue;
        consider(Math.max(gapLo + 6, Math.min(gapHi - 6, facing)));
        consider((gapLo + gapHi) / 2);
        // Park on the dest-facing lip of a gap (cluster alley) rather than its midpoint.
        if (hint?.preferDest) {
            if (startX < endX) consider(gapHi - 8);
            else consider(gapLo + 8);
        }
    }
    if (intervals.length > 0) {
        consider(intervals[0].lo - 8);
        consider(intervals[intervals.length - 1].hi + 8);
    } else {
        consider(startX + (Math.sign(endX - startX) || -1) * ORTHO_LANE);
    }

    const unique = [...new Set(candidates.map(x => Math.round(x * 2) / 2))];
    if (unique.length === 0) {
        const toward = Math.sign(endX - startX) || -1;
        return startX + toward * ORTHO_LANE;
    }

    unique.sort((a, b) => {
        const oa = gutterOvershoots(startX, endX, a, hint) ? 1 : 0;
        const ob = gutterOvershoots(startX, endX, b, hint) ? 1 : 0;
        if (oa !== ob) return oa - ob;
        const wa = farSrcWrap(startX, endX, a, hint) ? 1 : 0;
        const wb = farSrcWrap(startX, endX, b, hint) ? 1 : 0;
        if (wa !== wb) return wa - wb;
        const da = Math.abs(a - facing);
        const db = Math.abs(b - facing);
        if (Math.abs(da - db) > 4) return da - db;
        if (hint?.preferDest) return Math.abs(a - endX) - Math.abs(b - endX);
        return Math.abs(a - startX) - Math.abs(b - startX);
    });

    let chosen = unique[0];
    const lane = Math.min(1, Math.abs(laneIndex)) * 16;
    if (lane > 0) {
        const outside = chosen >= facing ? 1 : -1;
        const shifted = chosen + outside * lane;
        if (!overshootsDest(startX, endX, shifted)
            && !columnBlocked(shifted, y0, y1, nodes, sourceNodeId, targetNodeId)) {
            chosen = shifted;
        }
    }
    return chosen;
}

function orthoStemLabelPos(pts: number[]): { x: number; y: number } {
    if (pts.length < 4) return { x: pts[0] ?? 0, y: pts[1] ?? 0 };
    if (pts.length < 6) return { x: pts[0], y: pts[1] };
    const x0 = pts[0], y0 = pts[1], x1 = pts[2], y1 = pts[3];
    const len = Math.hypot(x1 - x0, y1 - y0);
    if (len < 28) return { x: x1, y: y1 };
    const t = 0.42;
    return { x: x0 + (x1 - x0) * t, y: y0 + (y1 - y0) * t };
}

function simplifyOrthoPoints(pts: number[]): number[] {
    if (pts.length < 4) return pts;
    const out: number[] = [pts[0], pts[1]];
    for (let i = 2; i < pts.length; i += 2) {
        const x = pts[i], y = pts[i + 1];
        if (Math.abs(x - out[out.length - 2]) < 0.6 && Math.abs(y - out[out.length - 1]) < 0.6) continue;
        out.push(x, y);
    }
    let changed = true;
    while (changed && out.length >= 8) {
        changed = false;
        for (let i = 2; i < out.length - 2; i += 2) {
            const x0 = out[i - 2], y0 = out[i - 1];
            const x1 = out[i], y1 = out[i + 1];
            const x2 = out[i + 2], y2 = out[i + 3];
            const colH = Math.abs(y0 - y1) < 0.6 && Math.abs(y1 - y2) < 0.6;
            const colV = Math.abs(x0 - x1) < 0.6 && Math.abs(x1 - x2) < 0.6;
            if (colH || colV) {
                out.splice(i, 2);
                changed = true;
                break;
            }
        }
    }
    return out;
}

function findClearHorizontalY(
    preferredY: number,
    x0: number,
    x1: number,
    nodes: Node[],
    sourceNodeId: string,
    targetNodeId: string,
    bias: number,
): number {
    if (!rowBlocked(preferredY, x0, x1, nodes, sourceNodeId, targetNodeId)) return preferredY;
    const dir = Math.sign(bias) || 1;
    let minY = preferredY;
    let maxY = preferredY;
    for (const node of nodes) {
        if (node.id === sourceNodeId || node.id === targetNodeId) continue;
        minY = Math.min(minY, node.position.y - 48);
        maxY = Math.max(maxY, node.position.y + node.size.height + 48);
    }
    const limit = Math.max(maxY - preferredY, preferredY - minY, 80);
    for (let d = CLEAR_STEP; d <= limit; d += CLEAR_STEP) {
        const first = preferredY + dir * d;
        const second = preferredY - dir * d;
        if (!rowBlocked(first, x0, x1, nodes, sourceNodeId, targetNodeId)) return first;
        if (!rowBlocked(second, x0, x1, nodes, sourceNodeId, targetNodeId)) return second;
    }
    return preferredY;
}

interface OrthoResult {
    linePoints: number[];
    labelPos: Point;
    arrowPenultimate: Point;
    endPoint: Point;
}

/**
 * Produce an H-V-H or V-H-V orthogonal path between two sockets.
 * Supports magnetic snap (grid) and basic obstacle avoidance.
 */
function getOrthogonalPath(
    startX: number, startY: number,
    endX: number, endY: number,
    srcExit: { dx: number; dy: number },
    tgtExit: { dx: number; dy: number },
    nodes: Node[],
    sourceNodeId: string,
    targetNodeId: string,
    waypoints: Point[],
    midShift = 0,
): OrthoResult {
    const endPoint = { x: endX, y: endY };

    if (waypoints.length > 0) {
        // Use waypoints snapped to grid as intermediate orthogonal anchors
        const snapped = waypoints.map(wp => ({ x: snapToGrid(wp.x), y: snapToGrid(wp.y) }));
        const allPts: Point[] = [{ x: startX, y: startY }, ...snapped, { x: endX, y: endY }];
        // Build orthogonal segments: H-V between each pair
        const pts: number[] = [allPts[0].x, allPts[0].y];
        for (let i = 1; i < allPts.length; i++) {
            const prev = allPts[i - 1];
            const cur = allPts[i];
            // H then V
            pts.push(cur.x, prev.y);
            pts.push(cur.x, cur.y);
        }
        const midIdx = Math.floor(pts.length / 4) * 2;
        const labelPos = { x: pts[midIdx] ?? (startX + endX) / 2, y: pts[midIdx + 1] ?? (startY + endY) / 2 };
        const penIdx = Math.max(0, pts.length - 4);
        const arrowPenultimate = { x: pts[penIdx], y: pts[penIdx + 1] };
        return { linePoints: pts, labelPos, arrowPenultimate, endPoint };
    }

    const srcHoriz = srcExit.dx !== 0;
    const tgtHoriz = tgtExit.dx !== 0;

    let pts: number[];

    if (srcHoriz && tgtHoriz) {
        pts = routeHVH(startX, startY, endX, endY, srcExit, tgtExit, nodes, sourceNodeId, targetNodeId, midShift);
    } else if (!srcHoriz && !tgtHoriz) {
        pts = routeVHV(startX, startY, endX, endY, srcExit, tgtExit, nodes, sourceNodeId, targetNodeId, midShift);
    } else if (srcHoriz) {
        if (columnBlocked(endX, startY, endY, nodes, sourceNodeId, targetNodeId)
            || rowBlocked(startY, startX, endX, nodes, sourceNodeId, targetNodeId)) {
            const gutterX = nearestClearVerticalX(
                startX, endX, startY, endY, nodes, sourceNodeId, targetNodeId,
                Math.round(midShift / ORTHO_LANE),
            );
            pts = [startX, startY, gutterX, startY, gutterX, endY, endX, endY];
        } else {
            pts = [startX, startY, endX, startY, endX, endY];
        }
    } else if (columnBlocked(startX, startY, endY, nodes, sourceNodeId, targetNodeId)
        || rowBlocked(endY, startX, endX, nodes, sourceNodeId, targetNodeId)) {
        const gutterX = nearestClearVerticalX(
            startX, endX, startY, endY, nodes, sourceNodeId, targetNodeId,
            Math.round(midShift / ORTHO_LANE),
        );
        pts = [startX, startY, gutterX, startY, gutterX, endY, endX, endY];
    } else {
        pts = [startX, startY, startX, endY, endX, endY];
    }

    pts = simplifyOrthoPoints(pts);

    // Sit the label on the first stem, not the shared bus, so sibling fans
    // (processing failure → DLQ) stay under their sources instead of stacking
    // on the rank-gap highway.
    const labelPos = orthoStemLabelPos(pts);
    const penIdx = Math.max(0, pts.length - 4);
    const arrowPenultimate = { x: pts[penIdx], y: pts[penIdx + 1] };

    return { linePoints: pts, labelPos, arrowPenultimate, endPoint };
}

function routeVHV(
    startX: number, startY: number,
    endX: number, endY: number,
    srcExit: { dx: number; dy: number },
    tgtExit: { dx: number; dy: number },
    nodes: Node[],
    sourceNodeId: string,
    targetNodeId: string,
    midShift: number,
): number[] {
    const travelY = endY - startY;
    const dirY = Math.sign(travelY) || Math.sign(srcExit.dy) || 1;
    const reverse = srcExit.dy * travelY < 0;
    const span = Math.abs(endY - startY);
    const srcNode = nodes.find(node => node.id === sourceNodeId);
    const tgtNode = nodes.find(node => node.id === targetNodeId);
    const interCluster = Boolean(srcNode && tgtNode && srcNode.parentGroupId !== tgtNode.parentGroupId);
    const boxes = interCluster ? clusterBoxesFromNodes(nodes) : null;
    const srcBox = srcNode?.parentGroupId ? boxes?.get(srcNode.parentGroupId) : undefined;
    const dstBox = tgtNode?.parentGroupId ? boxes?.get(tgtNode.parentGroupId) : undefined;
    // Reverse *same-cluster* wraps stay tight (DLQ → Kafka). Reverse *inter-cluster*
    // still needs neighbor frames as obstacles so the bus cannot tunnel through them.
    const obstacles = (reverse && !interCluster)
        ? withoutEnclosing(nodes, sourceNodeId, targetNodeId)
        : routingObstacles(nodes, sourceNodeId, targetNodeId);
    const shift = reverse ? 0 : cappedShift(midShift, startY, endY);
    const trunk = orthogonalTrunk(startX, startY, endX, endY, srcExit, tgtExit);
    const preferredY = clampBetween(trunk.mid + shift, startY, endY, 8);

    const aligned = Math.abs(endX - startX) < 8;
    if (aligned && !reverse && !columnBlocked((startX + endX) / 2, startY, endY, obstacles, sourceNodeId, targetNodeId)) {
        return [startX, startY, startX, endY, endX, endY];
    }

    const fourPoint = (busY: number): number[] | null => {
        const y = clampBetween(busY, startY, endY, 6);
        if (columnBlocked(startX, startY, y, obstacles, sourceNodeId, targetNodeId)) return null;
        if (columnBlocked(endX, y, endY, obstacles, sourceNodeId, targetNodeId)) return null;
        if (rowBlocked(y, startX, endX, obstacles, sourceNodeId, targetNodeId)) return null;
        return [startX, startY, startX, y, endX, y, endX, endY];
    };

    // Short next-rank hops should be a single L with the bus in the rank gap,
    // not a stub tucked under the source (that piles sibling labels together).
    // Inter-cluster must still outline: a 4-point drop at startX is what
    // punches through Event Streaming on topic → Stream Processor.
    if (!reverse && span < 240 && !interCluster) {
        if (Math.abs(endX - startX) < 32
            && !rowBlocked(startY, startX, endX, obstacles, sourceNodeId, targetNodeId)
            && !columnBlocked(endX, startY, endY, obstacles, sourceNodeId, targetNodeId)) {
            return [startX, startY, endX, startY, endX, endY];
        }
        const simpleY = clampBetween((startY + endY) / 2, startY, endY, 8);
        const simple = fourPoint(simpleY);
        if (simple) return simple;
    }

    // Same-cluster / ungrouped edges can use a 4-point L. Inter-cluster
    // edges always outline — a 4-point bus is what cut through Observability
    // on nearby reverse hops like Order Service → OTEL.
    if (!interCluster) {
        const hit = fourPoint(preferredY);
        if (hit) return hit;

        for (let d = CLEAR_STEP; d <= span; d += CLEAR_STEP) {
            const along = fourPoint(preferredY + dirY * d);
            if (along) return along;
            const back = fourPoint(preferredY - dirY * d);
            if (back) return back;
        }
    }

    const stub = CLEAR_PAD + 2;
    const longHaul = span > 400 || (interCluster && span > 200);
    const leaveDir = reverse ? dirY : (srcExit.dy * dirY > 0 ? srcExit.dy : dirY);
    let yLeave = reverse ? startY : startY + leaveDir * (longHaul ? 48 : stub);
    // Reverse / wrap: enter along the target socket row. Going past the node
    // then dropping back in is the U-turn on feedback edges like DLQ → Kafka.
    let yEnter = reverse
        ? endY
        : endY + (tgtExit.dy || -dirY) * (longHaul ? 56 : stub);

    if (!reverse && longHaul && dirY > 0) {
        if (srcBox) {
            const below = srcBox.y + srcBox.h + 12;
            if (below > startY && below < endY) yLeave = below;
        }
        yLeave = clampBetween(yLeave + Math.abs(shift), startY, endY, 8);
        if (dstBox) {
            const above = dstBox.y - 24;
            if (above > yLeave && above < endY) yEnter = above;
        }
        if (yEnter <= yLeave) yEnter = clampBetween(yLeave + 24, startY, endY, 8);
    } else if (!reverse && longHaul && dirY < 0) {
        if (srcBox) {
            const above = srcBox.y - 12;
            if (above < startY && above > endY) yLeave = above;
        }
        if (dstBox) {
            const below = dstBox.y + dstBox.h + 12;
            if (below < yLeave && below > endY) yEnter = below;
        }
    }

    const gutterHint: GutterHint | undefined = (interCluster && dstBox)
        ? {
            preferDest: true,
            destLeft: dstBox.x,
            destRight: dstBox.x + dstBox.w,
            srcLeft: srcBox ? srcBox.x - CLUSTER_LIP : undefined,
            srcRight: srcBox ? srcBox.x + srcBox.w + CLUSTER_LIP : undefined,
        }
        : undefined;

    let yStub = yLeave;
    if (!reverse && srcBox && dirY > 0) {
        yStub = clampBetween(startY + 20, startY, Math.min(endY, srcBox.y + srcBox.h - 8), 6);
    } else if (!reverse && srcBox && dirY < 0) {
        yStub = clampBetween(startY - 20, endY, startY, 6);
    }

    let gutterY0 = reverse ? yLeave : yStub;
    let srcClearY: number | undefined;
    if (!reverse && srcBox && dirY > 0) {
        const below = srcBox.y + srcBox.h + 12;
        if (below > yStub + 8 && below < yEnter) {
            srcClearY = below;
            gutterY0 = below;
        }
    } else if (!reverse && srcBox && dirY < 0) {
        const above = srcBox.y - 12;
        if (above < yStub - 8 && above > yEnter) {
            srcClearY = above;
            gutterY0 = above;
        }
    }

    let gutterX = nearestClearVerticalX(
        startX, endX, gutterY0, yEnter, obstacles, sourceNodeId, targetNodeId,
        reverse ? 0 : Math.round(midShift / ORTHO_LANE),
        gutterHint,
    );
    if (!reverse && srcBox) {
        gutterX = outsideClusterX(
            gutterX, srcBox, gutterY0, yEnter, obstacles, sourceNodeId, targetNodeId,
            startX, endX,
        );
    }
    if (gutterOvershoots(startX, endX, gutterX, gutterHint)) gutterX = towardDestX(startX, endX);
    if (reverse) {
        if (!interCluster) {
            return [startX, startY, gutterX, startY, gutterX, endY, endX, endY];
        }
        const yTop = dstBox ? dstBox.y - 24 : endY;
        let yArrive = endY;
        if (dstBox && tgtNode && dirY < 0) {
            const destAtClusterTop = tgtNode.position.y <= dstBox.y + CLUSTER_PAD_TOP + 28;
            if (destAtClusterTop) {
                const above = dstBox.y - 24;
                if (Math.abs(above - endY) <= 80) yArrive = above;
            }
        }
        if (yArrive === endY && rowBlocked(endY, gutterX, endX, obstacles, sourceNodeId, targetNodeId)) {
            const found = findClearHorizontalY(
                endY, gutterX, endX, obstacles, sourceNodeId, targetNodeId,
                tgtExit.dy || -dirY,
            );
            // A dest-cluster-top detour that then drops into a mid-cluster
            // node is a U-turn. Keep the last bus near the target socket.
            if (Math.abs(found - endY) <= 80) yArrive = found;
        }
        // Reverse from inside a source cluster below dest: exit the source
        // upward (dest-facing wall), H in the inter-row gutter, climb dest.
        if (
            srcBox && dstBox && dirY < 0
            && startY > srcBox.y + 8 && startY < srcBox.y + srcBox.h - 8
            && endY < srcBox.y - 8
        ) {
            const srcTop = srcBox.y - CLUSTER_LIP;
            const destGutter = startX < endX
                ? dstBox.x - CLUSTER_LIP
                : dstBox.x + dstBox.w + CLUSTER_LIP;
            const left = srcBox.x - CLUSTER_LIP;
            const right = srcBox.x + srcBox.w + CLUSTER_LIP;
            const destFacing = startX < endX ? right : left;
            const other = destFacing === right ? left : right;
            const sideOk = (x: number) =>
                !overshootsDest(startX, endX, x)
                && !columnBlocked(x, srcTop, startY, obstacles, sourceNodeId, targetNodeId);
            const srcSide = sideOk(destFacing) ? destFacing : (sideOk(other) ? other : null);
            if (
                srcSide != null
                && srcTop < startY - 8 && srcTop > yArrive + 8
                && !overshootsDest(startX, endX, destGutter)
                && !columnBlocked(destGutter, srcTop, yArrive, obstacles, sourceNodeId, targetNodeId)
            ) {
                const pts = [startX, startY];
                if (Math.abs(srcSide - startX) > 2) pts.push(srcSide, startY);
                pts.push(srcSide, srcTop);
                if (Math.abs(destGutter - srcSide) > 2) pts.push(destGutter, srcTop);
                pts.push(destGutter, yArrive, endX, yArrive);
                if (Math.abs(yArrive - endY) > 2) pts.push(endX, endY);
                return pts;
            }
        }
        // Source is below dest: if the dest-facing lip is blocked on the
        // full span (neighbor under dest), H in the inter-row gutter then
        // climb the dest outline. Avoids a sky-bus over Event Streaming
        // on Fulfillment → OTEL.
        if (dstBox && startY > dstBox.y + dstBox.h + 40) {
            const destGutter = startX < endX
                ? dstBox.x - CLUSTER_LIP
                : dstBox.x + dstBox.w + CLUSTER_LIP;
            const yBridge = dstBox.y + dstBox.h + CLUSTER_LIP;
            if (
                yBridge < startY - 16
                && !overshootsDest(startX, endX, destGutter)
                && columnBlocked(destGutter, startY, yArrive, obstacles, sourceNodeId, targetNodeId)
                && !columnBlocked(destGutter, yBridge, yArrive, obstacles, sourceNodeId, targetNodeId)
            ) {
                let lowerGutter = nearestClearVerticalX(
                    startX, destGutter, startY, yBridge, obstacles, sourceNodeId, targetNodeId,
                    0, gutterHint,
                );
                let yLow = startY;
                const towardMid = Math.sign(yBridge - startY) || -dirY;
                if (rowBlocked(startY, startX, lowerGutter, obstacles, sourceNodeId, targetNodeId)) {
                    const found = findClearHorizontalY(
                        startY, startX, lowerGutter, obstacles, sourceNodeId, targetNodeId,
                        towardMid,
                    );
                    if ((found - startY) * towardMid > 0) yLow = found;
                    else lowerGutter = startX;
                }
                let yMid = yBridge;
                if (rowBlocked(yBridge, lowerGutter, destGutter, obstacles, sourceNodeId, targetNodeId)) {
                    const found = findClearHorizontalY(
                        yBridge, lowerGutter, destGutter, obstacles, sourceNodeId, targetNodeId,
                        Math.sign(yArrive - yBridge) || -dirY,
                    );
                    if (found > dstBox.y + dstBox.h + 8 && found < startY - 8) yMid = found;
                }
                const pts = [startX, startY];
                if (Math.abs(yLow - startY) > 2) pts.push(startX, yLow);
                if (Math.abs(lowerGutter - startX) > 2) pts.push(lowerGutter, yLow);
                pts.push(lowerGutter, yMid);
                if (Math.abs(destGutter - lowerGutter) > 2) pts.push(destGutter, yMid);
                pts.push(destGutter, yArrive, endX, yArrive);
                if (Math.abs(yArrive - endY) > 2) pts.push(endX, endY);
                return pts;
            }
        }
        let yStartBus = startY;
        if (rowBlocked(startY, startX, gutterX, obstacles, sourceNodeId, targetNodeId)) {
            yStartBus = findClearHorizontalY(
                yTop, startX, gutterX, obstacles, sourceNodeId, targetNodeId,
                Math.sign(yTop - startY) || -dirY,
            );
        }
        const pts = [startX, startY];
        if (Math.abs(yStartBus - startY) > 2) pts.push(startX, yStartBus);
        pts.push(gutterX, yStartBus, gutterX, yArrive, endX, yArrive);
        if (Math.abs(yArrive - endY) > 2) pts.push(endX, endY);
        return pts;
    }
    let arriveY = yEnter;
    if (rowBlocked(arriveY, gutterX, endX, obstacles, sourceNodeId, targetNodeId)) {
        arriveY = findClearHorizontalY(
            arriveY, gutterX, endX, obstacles, sourceNodeId, targetNodeId, tgtExit.dy || -dirY,
        );
    }

    const throughSource = srcBox && yStub > srcBox.y + 4 && yStub < srcBox.y + srcBox.h - 4
        ? overlap1D(startX, gutterX, srcBox.x + 8, srcBox.x + srcBox.w - 8)
        : 0;
    const insideSource = Boolean(
        srcBox && yStub > srcBox.y + 4 && yStub < srcBox.y + srcBox.h - 4,
    );
    // Dest stacked under the source subgraph: drop in a member gap (Mermaid
    // channel) instead of busing to the frame wall then around.
    if (
        srcBox && srcNode && insideSource && srcClearY != null
        && dirY > 0
        && endY > srcBox.y + srcBox.h + 8
    ) {
        const members = nodes.filter(n =>
            n.parentGroupId === srcNode.parentGroupId && !n.id.startsWith('__cluster:'),
        );
        const destUnder = endX > srcBox.x - 48 && endX < srcBox.x + srcBox.w + 80;
        const lowerMembers = members.filter(n => n.position.y > startY + 8);
        const nextTop = lowerMembers.length
            ? Math.min(...lowerMembers.map(n => n.position.y))
            : srcBox.y + srcBox.h;
        if (destUnder && nextTop > startY + 24) {
            let hY = clampBetween((startY + nextTop) / 2 + 28, startY + 10, nextTop - 10, 4);
            const channelX = destBelowChannelX(
                startX, endX, hY, srcClearY, srcBox, members, sourceNodeId, targetNodeId,
            );
            if (channelX != null) {
                if (rowBlocked(hY, startX, channelX, members, sourceNodeId, targetNodeId)) {
                    const found = findClearHorizontalY(
                        hY, startX, channelX, members, sourceNodeId, targetNodeId, 1,
                    );
                    if (found > startY + 8 && found < nextTop - 4) hY = found;
                    else hY = -1;
                }
                if (hY > 0 && !columnBlocked(channelX, hY, srcClearY, members, sourceNodeId, targetNodeId)) {
                    const afterX = overshootsDest(startX, endX, endX, 16) ? towardDestX(startX, endX) : endX;
                    const pts = [startX, startY, startX, hY];
                    if (Math.abs(channelX - startX) > 2) pts.push(channelX, hY);
                    pts.push(channelX, srcClearY);
                    if (Math.abs(afterX - channelX) > 2) pts.push(afterX, srcClearY);
                    if (Math.abs(srcClearY - arriveY) > 2) pts.push(afterX, arriveY);
                    pts.push(endX, arriveY, endX, endY);
                    return pts;
                }
            }
        }
        const gapX = nearestMemberGapX(
            startX, endX, yStub, srcClearY, srcBox, members, sourceNodeId, targetNodeId,
        );
        if (gapX != null) {
            let afterX = endX;
            if (dstBox) {
                const destLeft = dstBox.x - CLUSTER_LIP;
                const destRight = dstBox.x + dstBox.w + CLUSTER_LIP;
                afterX = startX < endX ? destLeft : destRight;
                if (overshootsDest(startX, endX, afterX)) afterX = towardDestX(startX, endX);
            }
            // Dest sits under the source frame — drop out the bottom and
            // stay on the dest column. Do not wrap the source wall.
            if (endX > srcBox.x - 48 && endX < srcBox.x + srcBox.w + 80) afterX = endX;
            if (overshootsDest(startX, endX, afterX, 16)) afterX = towardDestX(startX, endX);
            const pts = [startX, startY, startX, yStub];
            if (Math.abs(gapX - startX) > 2) pts.push(gapX, yStub);
            pts.push(gapX, srcClearY);
            if (Math.abs(afterX - gapX) > 2) pts.push(afterX, srcClearY);
            if (Math.abs(srcClearY - arriveY) > 2) pts.push(afterX, arriveY);
            pts.push(endX, arriveY, endX, endY);
            return pts;
        }
    }
    // Exit the nearer dest-facing wall. Do not wrap the opposite lip when
    // the destination sits outside the source frame.
    if (srcBox && insideSource && (throughSource > 72 || Math.abs(gutterX - startX) > 72)) {
        const left = srcBox.x - CLUSTER_LIP;
        const right = srcBox.x + srcBox.w + CLUSTER_LIP;
        const destFacing = startX < endX ? right : left;
        const nearest = Math.abs(startX - left) <= Math.abs(startX - right) ? left : right;
        const destInsideX = endX > srcBox.x - 8 && endX < srcBox.x + srcBox.w + 8;
        let srcSide = destFacing;
        if (destInsideX && Math.abs(startX - destFacing) > Math.abs(startX - nearest) + 64) srcSide = nearest;
        const other = srcSide === right ? left : right;
        const clearY = srcClearY ?? arriveY;
        const sideOk = (x: number) =>
            !gutterOvershoots(startX, endX, x, gutterHint)
            && !columnBlocked(x, yStub, clearY, obstacles, sourceNodeId, targetNodeId);
        if (!sideOk(srcSide)) srcSide = sideOk(other) ? other : gutterX;
        if (gutterOvershoots(startX, endX, srcSide, gutterHint)) srcSide = gutterX;
        if (Math.abs(srcSide - gutterX) > 8) {
            const pts = [startX, startY, startX, yStub, srcSide, yStub, srcSide, clearY];
            if (Math.abs(clearY - arriveY) > 2) pts.push(gutterX, clearY, gutterX, arriveY);
            else pts.push(gutterX, arriveY);
            pts.push(endX, arriveY, endX, endY);
            return pts;
        }
    }

    return [startX, startY, startX, yStub, gutterX, yStub, gutterX, arriveY, endX, arriveY, endX, endY];
}

function routeHVH(
    startX: number, startY: number,
    endX: number, endY: number,
    srcExit: { dx: number; dy: number },
    tgtExit: { dx: number; dy: number },
    nodes: Node[],
    sourceNodeId: string,
    targetNodeId: string,
    midShift: number,
): number[] {
    const travelX = endX - startX;
    const dirX = Math.sign(travelX) || Math.sign(srcExit.dx) || 1;
    const shift = cappedShift(midShift, startX, endX);
    const trunk = orthogonalTrunk(startX, startY, endX, endY, srcExit, tgtExit);
    const preferredX = clampBetween(trunk.mid + shift, startX, endX, 8);

    const fourPoint = (busX: number): number[] | null => {
        const x = clampBetween(busX, startX, endX, 6);
        if (rowBlocked(startY, startX, x, nodes, sourceNodeId, targetNodeId)) return null;
        if (columnBlocked(x, startY, endY, nodes, sourceNodeId, targetNodeId)) return null;
        if (rowBlocked(endY, x, endX, nodes, sourceNodeId, targetNodeId)) return null;
        return [startX, startY, x, startY, x, endY, endX, endY];
    };

    const hit = fourPoint(preferredX);
    if (hit) return hit;

    const span = Math.abs(endX - startX);
    for (let d = CLEAR_STEP; d <= span; d += CLEAR_STEP) {
        const along = fourPoint(preferredX + dirX * d);
        if (along) return along;
        const back = fourPoint(preferredX - dirX * d);
        if (back) return back;
    }

    const stub = CLEAR_PAD + 2;
    const leaveDir = srcExit.dx * dirX > 0 ? srcExit.dx : dirX;
    const xLeave = startX + leaveDir * stub;
    let xEnter = endX + (tgtExit.dx || -dirX) * stub;
    const gutterY = findClearHorizontalY(
        (startY + endY) / 2, xLeave, xEnter, nodes, sourceNodeId, targetNodeId,
        Math.sign(endY - startY) || 1,
    );
    if (columnBlocked(xEnter, gutterY, endY, nodes, sourceNodeId, targetNodeId)) {
        xEnter = nearestClearVerticalX(
            startX, endX, gutterY, endY, nodes, sourceNodeId, targetNodeId,
            Math.round(midShift / ORTHO_LANE),
        );
    }
    return [startX, startY, xLeave, startY, xLeave, gutterY, xEnter, gutterY, xEnter, endY, endX, endY];
}

/**
 * Unified edge geometry that respects the routing mode.
 * Falls back to the per-edge setting, then the global default.
 */
export function getEdgeGeometryWithRouting(
    edge: Edge,
    sourceNode: Node,
    targetNode: Node,
    sourceSocket: SocketDefinition,
    targetSocket: SocketDefinition,
    globalRoutingMode: EdgeRoutingMode,
    allNodes: Node[],
    midShift = 0,
) {
    const routing = edge.routingMode ?? globalRoutingMode;

    if (routing === 'orthogonal') {
        const startX = sourceNode.position.x + sourceSocket.position.x;
        const startY = sourceNode.position.y + sourceSocket.position.y;
        const endX = targetNode.position.x + targetSocket.position.x;
        const endY = targetNode.position.y + targetSocket.position.y;

        const srcExit = getSocketOutwardDir(sourceSocket, sourceNode.size);
        const tgtExit = getSocketOutwardDir(targetSocket, targetNode.size);

        // Same rule as the batch path: an edge the layout routed is drawn along
        // the route the layout gave it. Both entry points must agree, or a
        // single-edge redraw would jump somewhere else than the one the whole
        // diagram was laid out into.
        const laidOut = autoRoutePoints(edge, { x: startX, y: startY }, { x: endX, y: endY });
        if (laidOut) {
            const linePoints = laidOut;
            const n = linePoints.length;
            return {
                linePoints,
                labelPos: orthoStemLabelPos(linePoints),
                arrowPenultimate: { x: linePoints[n - 4], y: linePoints[n - 3] },
                endPoint: { x: linePoints[n - 2], y: linePoints[n - 1] },
                useTension: false,
                isOrthogonal: true as const,
            };
        }

        const result = getOrthogonalPath(
            startX, startY, endX, endY,
            srcExit, tgtExit,
            allNodes, sourceNode.id, targetNode.id,
            edge.waypoints ?? [],
            midShift,
        );

        return {
            ...result,
            useTension: false,
            isOrthogonal: true as const,
        };
    }

    const geom = getEdgeGeometry(edge, sourceNode, targetNode, sourceSocket, targetSocket, midShift);
    return { ...geom, isOrthogonal: false as const };
}

// ── Jumper / intersection detection for orthogonal edges ────────────────

export interface JumperPoint {
    edgeId: string;
    x: number;
    y: number;
}

/**
 * Find intersection points between orthogonal edges.
 * Only detects crossings of horizontal vs vertical segments.
 */
export function findOrthogonalIntersections(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _edgePaths: { edgeId: string; points: number[] }[],
): JumperPoint[] {
    // Crossing hops add a bump at every orthogonal intersection and make
    // dense flowcharts unreadable. Keep crossings as simple overlaps.
    return [];
}

function conflicts(
    a: { mid: number; span0: number; span1: number; start?: number; end?: number; sourceKey?: string },
    b: { mid: number; span0: number; span1: number; start?: number; end?: number; sourceKey?: string },
): boolean {
    if (a.sourceKey && b.sourceKey && a.sourceKey === b.sourceKey) return false;
    if (!spansOverlap(a.span0, a.span1, b.span0, b.span1)) return false;
    if (Math.abs(a.mid - b.mid) < CHANNEL_PROXIMITY) return true;
    if (a.start != null && b.start != null && Math.abs(a.start - b.start) < CHANNEL_PROXIMITY) return true;
    if (a.end != null && b.end != null && Math.abs(a.end - b.end) < CHANNEL_PROXIMITY) return true;
    return false;
}

function connectedComponents<T extends { mid: number; span0: number; span1: number }>(items: T[]): T[][] {
    const n = items.length;
    const adj: number[][] = Array.from({ length: n }, () => []);
    for (let i = 0; i < n; i++) {
        for (let j = i + 1; j < n; j++) {
            if (conflicts(items[i], items[j])) {
                adj[i].push(j);
                adj[j].push(i);
            }
        }
    }
    const seen = new Array(n).fill(false);
    const comps: T[][] = [];
    for (let i = 0; i < n; i++) {
        if (seen[i]) continue;
        const stack = [i];
        seen[i] = true;
        const comp: T[] = [];
        while (stack.length) {
            const u = stack.pop()!;
            comp.push(items[u]);
            for (const v of adj[u]) {
                if (seen[v]) continue;
                seen[v] = true;
                stack.push(v);
            }
        }
        comps.push(comp);
    }
    return comps;
}

/**
 * Stagger shared trunks so parallel H-V-H / V-H-V runs (and matching bezier
 * fans) don't sit on top of each other. Overlap groups are colored independently
 * so a short local hop cannot compress spacing for a long-haul bus.
 */
export function computeOrthoMidShifts(
    edges: Edge[],
    nodes: Node[],
    defaultRoutingMode: EdgeRoutingMode = 'bezier',
): Record<string, number> {
    const lanePitch = defaultRoutingMode === 'orthogonal' ? ORTHO_LANE : BEZIER_LANE;
    const nodeById = new Map(nodes.map(node => [node.id, node]));
    type LaneItem = {
        id: string;
        kind: 'hvh' | 'vhv';
        mid: number;
        span0: number;
        span1: number;
        dir: number;
        gap: number;
        start: number;
        end: number;
        sourceKey: string;
    };
    const items: LaneItem[] = [];

    for (const edge of edges) {
        const src = nodeById.get(edge.sourceNodeId);
        const tgt = nodeById.get(edge.targetNodeId);
        const srcSock = src?.sockets.find(socket => socket.id === edge.sourceSocketId);
        const tgtSock = tgt?.sockets.find(socket => socket.id === edge.targetSocketId);
        if (!src || !tgt || !srcSock || !tgtSock) continue;
        const srcExit = getSocketOutwardDir(srcSock, src.size);
        const tgtExit = getSocketOutwardDir(tgtSock, tgt.size);
        const startX = src.position.x + srcSock.position.x;
        const startY = src.position.y + srcSock.position.y;
        const endX = tgt.position.x + tgtSock.position.x;
        const endY = tgt.position.y + tgtSock.position.y;
        const trunk = orthogonalTrunk(startX, startY, endX, endY, srcExit, tgtExit);
        if (trunk.kind === 'mixed') continue;
        const start = trunk.kind === 'vhv' ? startY : startX;
        const end = trunk.kind === 'vhv' ? endY : endX;
        items.push({
            id: edge.id,
            kind: trunk.kind,
            mid: trunk.mid,
            span0: trunk.span0,
            span1: trunk.span1,
            dir: trunk.dir,
            gap: Math.abs(end - start),
            start,
            end,
            sourceKey: `${edge.sourceNodeId}:${edge.sourceSocketId}`,
        });
    }

    const shifts: Record<string, number> = {};
    const byKind = new Map<'hvh' | 'vhv', LaneItem[]>();
    for (const item of items) {
        const list = byKind.get(item.kind) ?? [];
        list.push(item);
        byKind.set(item.kind, list);
    }

    byKind.forEach(list => {
        for (const comp of connectedComponents(list)) {
            if (comp.length < 2) continue;
            comp.sort((a, b) =>
                a.mid - b.mid
                || Math.min(a.span0, a.span1) - Math.min(b.span0, b.span1)
                || a.id.localeCompare(b.id),
            );
            const occupants: LaneItem[][] = [];
            const laneOf = new Map<string, number>();
            for (const item of comp) {
                let lane = occupants.findIndex(group =>
                    group.every(other => !conflicts(other, item)),
                );
                if (lane < 0) {
                    lane = occupants.length;
                    occupants.push([]);
                }
                occupants[lane].push(item);
                laneOf.set(item.id, lane);
            }
            const nLanes = occupants.length;
            if (nLanes < 2) continue;
            const longItems = comp.filter(item => item.gap >= 160);
            const spacing = longItems.length > 0
                ? lanePitch
                : Math.min(
                    lanePitch,
                    Math.max(12, (Math.max(24, Math.min(...comp.map(item => item.gap)) - 40)) / Math.max(1, nLanes - 1)),
                );
            for (const item of comp) {
                const lane = laneOf.get(item.id) ?? 0;
                let shift = lane * spacing * item.dir;
                const trunk = item.mid + shift;
                const forward = (item.end - item.start) * item.dir > 0;
                if (forward) {
                    const limit = item.end - item.dir * 12;
                    if (item.dir > 0 && trunk > limit) shift = limit - item.mid;
                    if (item.dir < 0 && trunk < limit) shift = limit - item.mid;
                }
                shifts[item.id] = shift;
            }
        }
    });
    return shifts;
}

// ── Lane separation on final routed paths ──────────────────────────────

/** Perpendicular spacing between two runs that would otherwise be collinear. */
const NUDGE_PITCH = 14;
/** Runs shorter than this are junction jogs; nudging them just adds wobble. */
const NUDGE_MIN_RUN = 24;
/**
 * A run touching a socket normally stays put, but path simplification collapses
 * a zero-length stub and leaves a cross-diagram haul sitting at index 0. Past
 * this length it is worth stepping off the socket with a fresh elbow.
 */
const ANCHORED_MIN_RUN = 96;

interface PathRun {
    edgeId: string;
    /** Point index where the run starts (x is at [index], y at [index + 1]). */
    index: number;
    vertical: boolean;
    fixed: number;
    lo: number;
    hi: number;
    shift: number;
    /** Which socket this run touches, if any — it needs an elbow to move. */
    anchored: 'start' | 'end' | null;
}

function segmentClear(vertical: boolean, fixed: number, lo: number, hi: number, obstacles: Node[]): boolean {
    for (const node of obstacles) {
        const { x, y } = node.position;
        const { width, height } = node.size;
        if (vertical) {
            if (fixed <= x - 4 || fixed >= x + width + 4) continue;
            if (hi <= y || lo >= y + height) continue;
        } else {
            if (fixed <= y - 4 || fixed >= y + height + 4) continue;
            if (hi <= x || lo >= x + width) continue;
        }
        return false;
    }
    return true;
}

/**
 * Would moving `run` onto `pos` keep the path clear of every node?
 *
 * The run is only half the question: a rectilinear path alternates orientation,
 * so the two segments either side stretch to meet the run's new line. Checking
 * the run alone let an approach slide past its own target and then spear back
 * up through it. Where the run touches a socket the "neighbour" is the elbow
 * this move creates, which the same formula covers.
 */
function moveClear(pts: number[], run: PathRun, pos: number, obstacles: Node[]): boolean {
    if (!segmentClear(run.vertical, pos, run.lo, run.hi, obstacles)) return false;
    const i = run.index;
    const hasPrev = i >= 2;
    const hasNext = i + 5 < pts.length;
    if (run.vertical) {
        const prevX = hasPrev ? pts[i - 2] : pts[i];
        const nextX = hasNext ? pts[i + 4] : pts[i + 2];
        return segmentClear(false, pts[i + 1], Math.min(prevX, pos), Math.max(prevX, pos), obstacles)
            && segmentClear(false, pts[i + 3], Math.min(pos, nextX), Math.max(pos, nextX), obstacles);
    }
    const prevY = hasPrev ? pts[i - 1] : pts[i + 1];
    const nextY = hasNext ? pts[i + 5] : pts[i + 3];
    return segmentClear(true, pts[i], Math.min(prevY, pos), Math.max(prevY, pos), obstacles)
        && segmentClear(true, pts[i + 2], Math.min(pos, nextY), Math.max(pos, nextY), obstacles);
}

/**
 * Pull apart orthogonal runs that ended up drawn on the same line.
 *
 * The router picks a clear gutter per edge independently, so long hauls
 * converge on the same column and the same approach row — six lines arriving
 * at one node read as one line, and there is no way to tell which sources are
 * involved. Separating after routing works no matter why the router chose that
 * line, which shifting the idealised trunk alone cannot do.
 *
 * Only interior runs move: the first and last stay welded to their sockets.
 * Because a rectilinear path alternates orientation, shifting one run only
 * lengthens or shortens its two neighbours, so the path stays orthogonal.
 */
export function separateOrthogonalLanes(
    paths: Map<string, number[]>,
    obstacles: Node[] = [],
    pitch = NUDGE_PITCH,
): Map<string, number[]> {
    const out = new Map<string, number[]>();
    paths.forEach((pts, id) => out.set(id, [...pts]));

    const runs: PathRun[] = [];
    out.forEach((pts, edgeId) => {
        const count = pts.length / 2 - 1;
        if (count < 2) return;
        for (let s = 0; s < count; s++) {
            const i = s * 2;
            const x0 = pts[i], y0 = pts[i + 1], x1 = pts[i + 2], y1 = pts[i + 3];
            const vertical = Math.abs(x0 - x1) < 0.5;
            const horizontal = Math.abs(y0 - y1) < 0.5;
            if (vertical === horizontal) continue;
            const lo = vertical ? Math.min(y0, y1) : Math.min(x0, x1);
            const hi = vertical ? Math.max(y0, y1) : Math.max(x0, x1);
            const anchored = s === 0 ? 'start' : s === count - 1 ? 'end' : null;
            if (hi - lo < (anchored ? ANCHORED_MIN_RUN : NUDGE_MIN_RUN)) continue;
            runs.push({ edgeId, index: i, vertical, fixed: vertical ? x0 : y0, lo, hi, shift: 0, anchored });
        }
    });

    // Greedy global lane assignment. Two runs conflict only when they overlap
    // along the line AND sit closer than a lane apart, so runs the router
    // already separated keep the position it chose; only genuine collisions
    // step aside, one pitch at a time.
    const minSep = pitch * 0.75;
    // Longest first: a cross-diagram haul has the fewest clear corridors, so it
    // should keep the lane it found and let short jogs move around it.
    const order = [...runs].sort((a, b) => (b.hi - b.lo) - (a.hi - a.lo));
    const placedAt = new Map<string, { pos: number; lo: number; hi: number }[]>();
    const bucketKey = (vertical: boolean, pos: number) => `${vertical ? 'v' : 'h'}:${Math.floor(pos / pitch)}`;
    const conflicts = (run: PathRun, pos: number): boolean => {
        const bucket = Math.floor(pos / pitch);
        for (let d = -1; d <= 1; d++) {
            for (const other of placedAt.get(`${run.vertical ? 'v' : 'h'}:${bucket + d}`) ?? []) {
                if (Math.abs(other.pos - pos) >= minSep) continue;
                if (other.lo < run.hi && run.lo < other.hi) return true;
            }
        }
        return false;
    };

    for (const run of order) {
        let clear: number | null = null;
        let unshared: number | null = null;
        // Wide enough to cross a crowded approach corridor: a hub like a
        // telemetry collector can have a dozen edges converging on one gutter.
        for (let step = 0; step <= 10 && clear === null; step++) {
            for (const offset of step === 0 ? [0] : [step * pitch, -step * pitch]) {
                const pos = run.fixed + offset;
                if (conflicts(run, pos)) continue;
                if (unshared === null) unshared = pos;
                if (!moveClear(out.get(run.edgeId)!, run, pos, obstacles)) continue;
                clear = pos;
                break;
            }
        }
        // Nothing is both free and unobstructed. Staying put is the better of
        // the two bad options — the router chose that line to clear the nodes,
        // and a short shared stretch reads better than a line disappearing
        // behind the box it is pointing at. Only a run that was already
        // obstructed has nothing to lose by taking an obstructed lane.
        const pos = clear
            ?? (moveClear(out.get(run.edgeId)!, run, run.fixed, obstacles) ? run.fixed : unshared ?? run.fixed);
        run.shift = pos - run.fixed;
        const key = bucketKey(run.vertical, pos);
        const list = placedAt.get(key);
        if (list) list.push({ pos, lo: run.lo, hi: run.hi });
        else placedAt.set(key, [{ pos, lo: run.lo, hi: run.hi }]);
    }

    const byEdge = new Map<string, PathRun[]>();
    for (const run of runs) {
        if (run.shift === 0) continue;
        const list = byEdge.get(run.edgeId);
        if (list) list.push(run);
        else byEdge.set(run.edgeId, [run]);
    }
    // Highest index first: inserting an elbow only moves the points after it,
    // so runs still waiting to be written keep the indices they were found at.
    byEdge.forEach((list, edgeId) => {
        const pts = out.get(edgeId)!;
        list.sort((a, b) => b.index - a.index);
        for (const run of list) {
            const axis = run.vertical ? 0 : 1;
            if (run.anchored === 'start') {
                // Duplicate the socket point so the run steps off it sideways.
                pts.splice(2, 0, pts[0], pts[1]);
                pts[2 + axis] += run.shift;
                pts[4 + axis] += run.shift;
            } else if (run.anchored === 'end') {
                const tail = pts.length - 2;
                pts.splice(tail, 0, pts[tail], pts[tail + 1]);
                pts[run.index + axis] += run.shift;
                pts[run.index + 2 + axis] += run.shift;
            } else {
                pts[run.index + axis] += run.shift;
                pts[run.index + 2 + axis] += run.shift;
            }
        }
    });

    out.forEach((pts, id) => out.set(id, simplifyOrthoPoints(pts)));
    return out;
}

/**
 * Anchor a label on the longest clear run of a path rather than on the stem.
 *
 * The stem is the worst place for it: it is short, it sits hard against the
 * source node, and on a dense diagram the text lands on top of that node. The
 * longest run is the one stretch with room to read.
 */
function orthoOpenLabelPos(pts: number[], obstacles: Node[]): { x: number; y: number } {
    if (pts.length < 6) return orthoStemLabelPos(pts);
    let best = -1;
    let bestLen = 0;
    for (let i = 0; i + 3 < pts.length; i += 2) {
        const len = Math.abs(pts[i + 2] - pts[i]) + Math.abs(pts[i + 3] - pts[i + 1]);
        // Skip the two stubs; they belong to the node, not the run.
        if (i === 0 || i + 4 >= pts.length) continue;
        if (len > bestLen) { bestLen = len; best = i; }
    }
    if (best < 0 || bestLen < 40) return orthoStemLabelPos(pts);
    const mid = { x: (pts[best] + pts[best + 2]) / 2, y: (pts[best + 1] + pts[best + 3]) / 2 };
    const clear = !obstacles.some(node =>
        mid.x > node.position.x - 6 && mid.x < node.position.x + node.size.width + 6 &&
        mid.y > node.position.y - 6 && mid.y < node.position.y + node.size.height + 6);
    return clear ? mid : orthoStemLabelPos(pts);
}

export interface RoutedOrthoEdge {
    linePoints: number[];
    labelPos: Point;
    arrowPenultimate: Point;
    endPoint: Point;
}

/**
 * Route every orthogonal edge once, as a set, so shared corridors can be
 * separated before anything is drawn. Edges left out of the result (bezier,
 * or missing a socket) fall back to per-edge geometry at render time.
 */
export function computeOrthoPaths(
    edges: Edge[],
    nodes: Node[],
    defaultRoutingMode: EdgeRoutingMode,
    midShifts: Record<string, number> = {},
): Record<string, RoutedOrthoEdge> {
    const nodeById = new Map(nodes.map(node => [node.id, node]));
    const routed = new Map<string, number[]>();
    const layoutRouted = new Set<string>();

    for (const edge of edges) {
        if ((edge.routingMode ?? defaultRoutingMode) !== 'orthogonal') continue;
        const src = nodeById.get(edge.sourceNodeId);
        const tgt = nodeById.get(edge.targetNodeId);
        if (!src || !tgt) continue;
        const srcSock = src.sockets.find(socket => socket.id === edge.sourceSocketId);
        const tgtSock = tgt.sockets.find(socket => socket.id === edge.targetSocketId);
        if (!srcSock || !tgtSock) continue;
        // An edge the layout routed is drawn along the route the layout gave
        // it: ordering, placement and routing were decided together there, and
        // nothing this pass can work out afterwards improves on that.
        const laidOut = autoRoutePoints(
            edge,
            { x: src.position.x + srcSock.position.x, y: src.position.y + srcSock.position.y },
            { x: tgt.position.x + tgtSock.position.x, y: tgt.position.y + tgtSock.position.y },
        );
        if (laidOut) {
            routed.set(edge.id, laidOut);
            layoutRouted.add(edge.id);
            continue;
        }
        const geom = getEdgeGeometryWithRouting(
            edge, src, tgt, srcSock, tgtSock, 'orthogonal', nodes, midShifts[edge.id] ?? 0,
        );
        routed.set(edge.id, geom.linePoints);
    }

    // Lane separation exists to pull apart lanes the fallback router had to
    // share. An edge that came out of a layout run was already given a lane of
    // its own there, so nudging it would only undo that.
    const toSeparate = new Map<string, number[]>();
    routed.forEach((pts, id) => { if (!layoutRouted.has(id)) toSeparate.set(id, pts); });
    const separated = separateOrthogonalLanes(toSeparate, nodes);
    routed.forEach((pts, id) => { if (layoutRouted.has(id)) separated.set(id, pts); });

    const result: Record<string, RoutedOrthoEdge> = {};
    separated.forEach((pts, id) => {
        const n = pts.length;
        result[id] = {
            linePoints: pts,
            labelPos: orthoOpenLabelPos(pts, nodes),
            arrowPenultimate: n >= 4 ? { x: pts[n - 4], y: pts[n - 3] } : { x: pts[0], y: pts[1] },
            endPoint: { x: pts[n - 2], y: pts[n - 1] },
        };
    });
    return result;
}
